import type { BulkResultResponse } from "@eduviaos/shared";

/**
 * Phase E1.b.3a.2 — pure summariser for the BE's per-item bulk-result
 * envelope.
 *
 * The Admissions bulk endpoints (advance / assign / delete) always
 * return HTTP 200 with a `BulkResultResponse` ({ successful, failed,
 * results[] }) so the FE can decide row-by-row what to do. This helper
 * collapses that envelope into the single decision the IA's
 * `BulkActionBar` needs to make:
 *
 *   - `kind === "all-ok"`     → fire `toast.success(toastMessage)`. No
 *     banner, no recovery context to anchor on screen, success rows
 *     drop out of the selection so the toolbar disappears.
 *   - `kind === "partial"`    → render the inline partial-result banner
 *     above the toolbar. The per-row failure list is structured
 *     recovery copy and must stay anchored to the still-selected
 *     failing rows (decision-rule from E1.b.2 / .3 / .4.{1,a,b} carries
 *     forward verbatim — moving structured recovery copy to a global
 *     toast in the bottom-right would unanchor "this enquiry failed
 *     because X" from the row it refers to).
 *   - `kind === "all-failed"` → identical render path to "partial", but
 *     the discriminator is preserved so future telemetry can split the
 *     two without changing the UI surface.
 *
 * Lives in its own file so the consumer (`BulkActionBar.tsx`) stays a
 * pure-React surface (react-refresh / fast-refresh requires single-
 * concern modules to short-circuit HMR), and so the helper can be
 * driven from Vitest without standing up the full BulkActionBar React
 * tree.
 */

export interface BulkResultSummaryFailure {
  enquiry_id: string;
  reason: string;
}

export type BulkResultSummary =
  | {
      kind: "all-ok";
      successful: number;
      failed: 0;
      failures: BulkResultSummaryFailure[];
      succeededIds: string[];
      actionLabel: string;
      /** Page-level message routed through `toast.success`. */
      toastMessage: string;
    }
  | {
      kind: "partial" | "all-failed";
      successful: number;
      failed: number;
      failures: BulkResultSummaryFailure[];
      succeededIds: string[];
      actionLabel: string;
    };

export function summariseBulkResult(
  result: BulkResultResponse,
  actionLabel: string,
): BulkResultSummary {
  const items = result.results ?? [];
  const succeededIds = items.filter((i) => i.success).map((i) => i.enquiry_id);
  const failures: BulkResultSummaryFailure[] = items
    .filter((i) => !i.success)
    .map((i) => ({
      enquiry_id: i.enquiry_id,
      reason: i.error_message ?? i.error_code ?? "Unknown error",
    }));
  const successful = result.successful;
  const failed = result.failed;
  if (failed === 0) {
    return {
      kind: "all-ok",
      successful,
      failed: 0,
      failures,
      succeededIds,
      actionLabel,
      toastMessage: `${successful} ${actionLabel}.`,
    };
  }
  return {
    kind: successful === 0 ? "all-failed" : "partial",
    successful,
    failed,
    failures,
    succeededIds,
    actionLabel,
  };
}
